import React, { useEffect, useState } from "react";
import { AssignmentListDataTable } from "./assignment-list-data-table";
import { type SortModel } from "@/react/prop-types";
import { useGroupContext } from "@/react/providers/group-context-provider";
import type { FindAssignmentsPaginatedQueryParams } from "@laborchart-modules/lc-core-api";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import {
   columnHeadersMap,
   convertAssignmentDataTableFilters,
   convertDataTableSort,
   defaultAssignmentListTableConfig,
   entityTypes,
   filterFieldMaps,
   getFormattedCustomFields,
   mapApiAssignmentToTableAssignment,
   updateAssignment,
} from "./assignment-list-helpers";
import { useViewPreferenceContext } from "@/react/providers/permission-context-provider";
import { PersonTearsheetProvider } from "../tearsheets/people/people-tearsheet";
import { FileDownloadToastProvider } from "../data-table/DataExport/FileDownload";
import { Box, useI18nContext, ToolLandingPage, SplitViewCard } from "@procore/core-react";
import type { TableApi } from "@procore/data-table";
import { ProjectTearsheetProvider } from "../tearsheets/project/project-tearsheet";
import "./style.css";
import { convertSavedViewToDataTableConfig } from "@/react/shared/helper";
import type { AssignmentListServerFilter } from "./assignment-list-prop-types";
import {
   streamJobTitles,
   streamPeople,
   streamViewableStatuses,
} from "../data-table/data-table-utils";
import { streamProjects } from "../common/queries/queries";
import { useGetCustomFields, useGetSavedView } from "../common/queries/queries";
import { useGetProjectRoleOptions } from "../project-list/queries";
import { AssignmentTearsheetProvider } from "../tearsheets/assignment/assignment-tearsheet";
import type { SavedView } from "@laborchart-modules/common";
import { filterRendererMap } from "./assignment-list-filter-renderer-map";
import { useGetAssignmentCreationSupportDataQuery } from "../tearsheets/assignment/queries";

export function AssignmentListContainer({ setTitle }: { setTitle: (title: string) => void }) {
   const I18n = useI18nContext();
   const { groupId } = useGroupContext();
   const { getViewPreference } = useViewPreferenceContext();
   const isLastNameFirst = getViewPreference()?.displayLastNamesFirst() ?? false;
   const [tableApi, setTableApi] = useState<TableApi>();
   const { data: customFields } = useGetCustomFields(entityTypes);

   const { data: roleOptions } = useGetProjectRoleOptions(groupId);
   const { data: assignmentCreationSupportData } =
      useGetAssignmentCreationSupportDataQuery(groupId);
   // Format custom fields for the data table adding entity type (Assignment, Project, Person)
   const formattedCustomFields = getFormattedCustomFields(customFields ?? null);
   const handleTableReady = (api: TableApi) => setTableApi(api);

   const [convertedSavedView, setConvertedSavedView] = useState<any>();
   const url = new URL(window.location.href);
   const params = url.searchParams;
   const [viewId] = useState(params.get("viewId"));
   const { data: savedViewData } = useGetSavedView(viewId);

   /* istanbul ignore next */
   useEffect(() => {
      if (viewId && savedViewData && customFields && formattedCustomFields && !convertedSavedView) {
         const convertedSavedViewData = convertSavedViewToDataTableConfig(
            savedViewData?.data as SavedView,
            columnHeadersMap,
            defaultAssignmentListTableConfig,
            filterFieldMaps,
            filterRendererMap,
            formattedCustomFields,
         );
         setConvertedSavedView(convertedSavedViewData);
         setTitle(
            savedViewData.data?.name ??
               `${I18n.t("views.company.workforce_planning.list")} (${I18n.t(
                  "views.company.workforce_planning.beta",
               )})`,
         );
      }
   }, [
      groupId,
      viewId,
      savedViewData,
      convertedSavedView,
      setConvertedSavedView,
      customFields,
      formattedCustomFields,
      setTitle,
   ]);

   /* istanbul ignore next */
   const fetchAssignmentList = async (
      filters: AssignmentListServerFilter[],
      sortModel: SortModel[],
      startingAfter?: string,
   ) => {
      const coreApiFilters = convertAssignmentDataTableFilters(filters);
      const coreApiSort = convertDataTableSort(sortModel, isLastNameFirst);
      const params: FindAssignmentsPaginatedQueryParams = {
         timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
         limit: 100,
         starting_after: startingAfter,
         sort_by: coreApiSort.sort_by,
         sort_direction: coreApiSort.direction,
         group_id: groupId === "my-groups" ? undefined : groupId,
         custom_field_id: coreApiSort.custom_field_id, // required when sorted by custom field
         filters: coreApiFilters,
      };

      const assignmentData = await Assignment2Store.getCombinedAssignments(params).payload;
      const assignmentListDetails = mapApiAssignmentToTableAssignment(
         assignmentData.data,
         I18n,
         assignmentCreationSupportData,
      );

      return { data: assignmentListDetails, pagination: assignmentData.pagination };
   };

   return (
      <ProjectTearsheetProvider projectsTableApi={tableApi}>
         <AssignmentTearsheetProvider assignmentTableApi={tableApi}>
            <PersonTearsheetProvider peopleTableApi={tableApi}>
               <FileDownloadToastProvider>
                  <ToolLandingPage
                     className={
                        "border-box assignment-list-detail-page data-table-list-detail-page"
                     }
                  >
                     <ToolLandingPage.Main className="assignment-list-page-main">
                        <ToolLandingPage.Body className="assignment-list-page-body">
                           <SplitViewCard className="assignment-list-card">
                              <SplitViewCard.Section className="assignment-list-section data-table-list-section">
                                 <Box
                                    style={{
                                       marginTop: "5px",
                                       height: "100%",
                                    }}
                                 >
                                    {assignmentCreationSupportData?.data &&
                                       ((!viewId &&
                                          customFields &&
                                          formattedCustomFields &&
                                          roleOptions) ||
                                          (viewId &&
                                             convertedSavedView &&
                                             customFields &&
                                             formattedCustomFields &&
                                             roleOptions)) && (
                                          <AssignmentListDataTable
                                             fetchAssignmentList={fetchAssignmentList}
                                             tableApi={tableApi}
                                             handleTableReady={handleTableReady}
                                             groupId={groupId}
                                             streamJobTitles={streamJobTitles}
                                             streamViewableStatuses={streamViewableStatuses}
                                             streamProjects={streamProjects}
                                             streamPeople={streamPeople}
                                             customFields={formattedCustomFields}
                                             roleOptions={roleOptions}
                                             savedView={viewId ? convertedSavedView : null}
                                             isLastNameFirst={isLastNameFirst}
                                             assignmentCreationSupportData={
                                                assignmentCreationSupportData
                                             }
                                             updateAssignment={(params) =>
                                                updateAssignment(params, customFields)
                                             }
                                          />
                                       )}
                                 </Box>
                              </SplitViewCard.Section>
                           </SplitViewCard>
                        </ToolLandingPage.Body>
                     </ToolLandingPage.Main>
                  </ToolLandingPage>
               </FileDownloadToastProvider>
            </PersonTearsheetProvider>
         </AssignmentTearsheetProvider>
      </ProjectTearsheetProvider>
   );
}
